import {
  LOCATION_TYPES,
  type CreateLocationRequest,
  type LocationDto,
  type LocationType,
} from '@gsls/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorNote,
  Field,
  PageHeader,
  Spinner,
  Tooltip,
  inputClass,
} from '../components/ui';
import { CodeField } from '../components/CodeField';
import { DataTable, type DataColumn } from '../components/DataTable';
import { FilterBar } from '../components/FilterBar';
import { ApiError, api } from '../lib/api';
import { humanise } from '../lib/format';
import { Can, useSession } from '../lib/session';

const TYPE_TONES: Record<LocationType, 'green' | 'accent' | 'hazard' | 'muted' | 'dark'> = {
  warehouse: 'green',
  production: 'accent',
  quarantine: 'hazard',
  scrap: 'muted',
  lab: 'dark',
};

const EMPTY = { code: '', name: '', type: 'warehouse' as LocationType, requiresQcRelease: false };

export function LocationsPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const canManage = can('masterdata.manage');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const listQuery = useQuery({
    queryKey: ['locations', { includeInactive }],
    queryFn: () => api.locations.list({ includeInactive, limit: 100 }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['locations'] });

  const createMutation = useMutation({
    mutationFn: (payload: CreateLocationRequest) => api.locations.create(payload),
    onSuccess: () => {
      void invalidate();
      setForm(EMPTY);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (location: LocationDto) =>
      api.locations.update(location.id, { isActive: !location.isActive }),
    onSuccess: () => void invalidate(),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    createMutation.mutate({
      code: form.code.trim(),
      name: form.name.trim(),
      type: form.type,
      requiresQcRelease: form.requiresQcRelease,
    });
  }

  const locations = listQuery.data?.items ?? [];
  const error = createMutation.error ?? toggleMutation.error ?? listQuery.error;

  const locationColumns: DataColumn<LocationDto>[] = [
    {
      key: 'code',
      header: 'Code',
      cell: (location) => <span className="font-mono text-xs">{location.code}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      primary: true,
      cell: (location) => <span className="font-medium text-ink">{location.name}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      cell: (location) => (
        <Badge tone={TYPE_TONES[location.type]}>{humanise(location.type)}</Badge>
      ),
    },
    {
      key: 'qc',
      header: 'QC release',
      cell: (location) => (location.requiresQcRelease ? 'Required' : '—'),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (location) =>
        location.isActive ? (
          <Badge tone="green">Active</Badge>
        ) : (
          <Badge tone="muted">Inactive</Badge>
        ),
    },
  ];

  const locationActions = canManage
    ? (location: LocationDto) => (
        <Button
          tone={location.isActive ? 'danger' : 'ghost'}
          onClick={() => toggleMutation.mutate(location)}
        >
          {location.isActive ? 'Deactivate' : 'Reactivate'}
        </Button>
      )
    : undefined;

  return (
    <>
      <PageHeader
        eyebrow="Master data"
        title="Locations"
        description="Where stock physically sits. A location flagged for QC release acts as a hold: material in it is visible and valued, but cannot be drawn into production until QC passes it."
      />

      <Can capability="masterdata.manage">
        <Card className="mb-6">
          <CardBody>
            <h2 className="text-base font-bold text-ink">New location</h2>
            <form
              autoComplete="off"
              onSubmit={handleSubmit}
              className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
            >
              <CodeField
                label="Code"
                value={form.code}
                onChange={(code) => setForm((current) => ({ ...current, code }))}
                queryKey={['location-suggest', form.type, form.name.trim()]}
                queryFn={
                  form.name.trim().length >= 2
                    ? () => api.locations.suggestCode({ type: form.type, name: form.name.trim() })
                    : undefined
                }
                tooltip="Built from the location type and the part of the name that is not the type — a warehouse called “Warehouse A” suggests WH-A. Accept the suggestion or type your own; it is fixed once created."
                error={
                  createMutation.error instanceof ApiError && createMutation.error.status === 409
                    ? createMutation.error.message
                    : undefined
                }
              />
              <Field label="Name">
                <input
                  className={inputClass}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="Quarantine Hold"
                />
              </Field>
              <Field
                label="Type"
                tooltip="What the location is for. It is descriptive — the rule that actually holds stock back is the QC switch next to it, not the type."
              >
                <select
                  className={inputClass}
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value as LocationType })}
                >
                  {LOCATION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {humanise(type)}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm text-ink-body">
                  <input
                    name="requiresQcRelease"
                    type="checkbox"
                    checked={form.requiresQcRelease}
                    onChange={(e) => setForm({ ...form, requiresQcRelease: e.target.checked })}
                  />
                  Requires QC release
                </label>
                <Tooltip
                  subject="Requires QC release"
                  text="Anything received into this location is put on hold. It is counted and valued like any other stock, but a batch cannot draw on it until someone passes a quality check, which releases the whole lot. Standard for a quarantine area or an incoming-goods bay."
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-4">
                <ErrorNote>
                  {createMutation.error instanceof ApiError ? createMutation.error.message : ''}
                </ErrorNote>
                <div className="mt-3">
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending ? 'Creating…' : 'Create location'}
                  </Button>
                </div>
              </div>
            </form>
          </CardBody>
        </Card>
      </Can>

      <Card>
        <CardBody>
          <FilterBar>
            <div className="mb-4 flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-ink-body">
                <input
                  name="includeInactive"
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
                Include inactive
              </label>
              {listQuery.isSuccess ? (
                <span className="text-sm text-ink-muted">{listQuery.data.total} location(s)</span>
              ) : null}
            </div>
          </FilterBar>

          {listQuery.isPending ? <Spinner /> : null}
          <ErrorNote>
            {error instanceof ApiError ? error.message : listQuery.isError ? 'Could not load locations.' : ''}
          </ErrorNote>

          {listQuery.isSuccess ? (
            <DataTable
              rows={locations}
              columns={locationColumns}
              rowKey={(location) => location.id}
              rowClassName={() => 'hover:bg-brand-soft/40'}
              actionsHeader="Actions"
              actions={locationActions}
              empty="No locations yet."
            />
          ) : null}
        </CardBody>
      </Card>
    </>
  );
}
